import { Fragment, useContext, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@apollo/client';
import { PlusIcon } from '../../../../assets/icons';
import { EllipsisVerticalIcon, FlagIcon, MagnifyingGlassIcon } from '@heroicons/react/24/solid';
import Flyout, { useFlyout } from 'components/Flyout/Flyout';
import New from './New';
import Squawk from 'components/Squawk/Squawk';
import { GetSquawksDocument } from 'graphql/generated';
import { useSession } from 'contexts/SessionContext';
import Button from 'components/Button/Button';
import Tabs from 'components/Tabs/Tabs';
import useSaveState from 'hooks/useSaveState';
import { Menu, Transition } from '@headlessui/react';
import { OrgSubtypes } from 'utils/orgTypes';
import Paywall from 'components/Paywall/Paywall';
import isMobileApp from 'utils/isMobileApp';
import Components from './Components';
import { useNavigate } from 'react-router-dom';
import { AbilityContext } from 'contexts/AbilityContext';
import { permissionHandler } from 'utils/PermissionHandler';
import sanitizeQuery from 'utils/querySanitizer';
import Header from 'components/Header/Header';
import SimpleSearch from 'components/SimpleSearch/SimpleSearch';
import CogMenu from 'components/CogMenu/CogMenu';
import ThumbButton from 'components/ThumbButton/ThumbButton';

const Squawks = () => { 
  const { craftId, organizationSubtype } = useSession();
  // check if craftId is undefined and navigate to crafts page
  const navigate = useNavigate();
  if(!craftId) navigate('/crafts');

  const { flyoutProps, updateFlyout, closeFlyout } = useFlyout();
  const [activeTab, setActiveTab] = useSaveState('squawksTab', 'All');
  const tabs = [ 'All', 'Open', 'Closed' ];
  const [query, setQuery] = useState('');
  const { data, refetch } = useQuery(GetSquawksDocument, { variables: 
    { craftId },
    skip: craftId === undefined
  });
  const ability = useContext(AbilityContext);
  const canManageSquawks = permissionHandler('Manage Squawks', ability);
  const menuItems = [
    { 
      mobileAction: async () => { await navigator.share({ title:"Export Squawks from Coflyt", url: process.env.REACT_APP_API_URL+"/squawks/"+craftId, text: "Export Squawks from Coflyt"}) },
      webAction: process.env.REACT_APP_API_URL+"/squawks/"+craftId,
      name: 'Export',
      webOnly: false
    },
    {
      webAction: () => updateFlyout({ title: 'Components', content: <Components/> }),
      name: "Components"
    }
  ]

  useEffect(() => {
    const sanitizedQuery = sanitizeQuery(query);
    const delayDebounceFn = setTimeout(() => {
      refetch(
        {
          where: { ...(query && { title: { startsWith: sanitizedQuery } }), craftId: { equals: craftId } },
        }
      );
    }, 400);
    return () => clearTimeout(delayDebounceFn);
  }, [query, refetch]);

  const tableData = useMemo(() => {
    let statuses = [];
    switch (activeTab) {
      case 'All':
        return data?.squawks;
      case 'Open':
        statuses = ['in_progress', 'open', 'planned', 'stalled'];
        break;
      case 'Closed':
        statuses = ['resolved'];
        break;
    }
    return data?.squawks?.filter((item) => statuses.includes(item?.status.toLowerCase()));
  }, [data?.squawks, activeTab]);

  return (
    <>
      <Flyout {...flyoutProps} />
      <div>
        <div className="flex flex-col">
          {/* Header */}
          <Header title="Squawks" icon={FlagIcon}>
            {organizationSubtype !== OrgSubtypes.FREE &&
            <>
              <div className="flex justify-end gap-2 flex-wrap order-2 lg:order-3">
                <div className="flex items-stretch gap-2">
                {canManageSquawks && 
                  <>
                    <Button className="hidden md:flex" text="Add Squawk" color="navy" size="xs" icon={<PlusIcon className="h-3 w-3 mr-1" />}
                      onClick={ () => updateFlyout({ title: 'Add Squawk', content: <New closeFlyout={() => {
                        refetch();
                        closeFlyout(); 
                      }}/> }) } 
                    />
                    <ThumbButton size="primary" role="add" onClick={ () => updateFlyout({ title: 'Add Squawk', content: <New closeFlyout={() => {
                        refetch();
                        closeFlyout(); 
                      }}/> }) } 
                    />
                  </>
                }
                  <CogMenu items={menuItems} />
                </div>
              </div>
              <SimpleSearch query={query} setQuery={setQuery} className="mt-2 pt-2 border-t border-slate-200 w-full order-3 lg:order-2 lg:mt-0 lg:mr-2 lg:pt-0 lg:border-t-0 lg:w-auto" />
            </>
            }
          </Header>
        </div>
        {organizationSubtype === OrgSubtypes.FREE ? <Paywall />
        :
        <>
          {/* Squawks List */}
          <div className={`flex`}>
            <div className="w-full">
              <div className="rounded bg-white rounded border border-slate-300">
                <div className="flex justify-between items-center border-b pt-1 bg-slate-50 w-full px-4 mb-3 rounded-t">
                  <Tabs setActiveItem={setActiveTab} activeItem={activeTab} items={tabs} />
                </div> 
                {tableData?.map((squawk, index, arr) => {
                  let last = arr.length - 1 === index ? true : false;
                  return <Squawk key={index} { ...{ squawk, updateFlyout, last, closeFlyout, refetch } }/>;
                })}
                {tableData?.length === 0 && <div className="flex justify-center items-center p-6">
                  <h1 className="text-center text-slate-500">No Squawks Found</h1>
                </div>}
              </div>
            </div>
          </div>
        </>
        }
      </div>
    </>
  );
};

export default Squawks;
